import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../../../lib/auth-context.js';
import { usePersistentState } from '../../../lib/persistent-state.js';
import { ActiveFilterStrip } from '../components/ActiveFilterStrip.js';
import { ScenarioDetailModal } from '../components/ScenarioDetailModal.js';
import {
  applyScenarioFilter,
  decodeScenarioFilter,
  encodeScenarioFilter,
  ScenarioFilterDrawer,
  type ScenarioFilterValue,
} from '../components/ScenarioFilterDrawer.js';
import { ScenarioListTable } from '../components/ScenarioListTable.js';
import {
  FOLDER_ALL,
  FOLDER_UNCATEGORIZED,
  ScenarioPageHeader,
  TEAM_ALL,
} from '../components/ScenarioPageHeader.js';
import { ScenarioToolbar } from '../components/ScenarioToolbar.js';
import { WorkfrontObjectsAllTable } from '../components/WorkfrontObjectsAllTable.js';
import { downloadCsv, scenariosToCsv } from '../export-csv.js';
import { useTeamsInfo } from '../../common/admin-hooks.js';
import {
  useScenarioCustomFields,
  useScenarioPlanningObjects,
  useScenariosList,
  useScenarioWorkfrontObjects,
} from '../hooks.js';
import type {
  ScenarioRow as ScenarioRowData,
  ScenarioSortDirection,
  ScenarioSortField,
  ScenarioStatusFilter,
} from '../types.js';
import { asScenarioRow, isActive } from '../types.js';
import { matchesSearch } from '../utils.js';

/**
 * Scenario list page — same composition for Business / Administrator
 * / Developer routes. Engine returns one list shape, so the `role`
 * prop only controls:
 *   - whether the view-dropdown (`Default` / `Workfront Objects`) is
 *     rendered (Admin / Developer only)
 *   - whether the expand-chevron column + sub-tables are surfaced
 *     (also Admin / Developer only — Business stays compact)
 *
 * Sub-tab data (`workfront-objects-data`, `custom-fields-data`) is
 * fetched license-wide once and grouped client-side by scenario
 * fusion-id; the fetch is gated on the `role` so the Business
 * Executive view doesn't pay the round-trip.
 */
export function ScenarioListPage({
  role,
}: {
  role: 'business' | 'administrator' | 'developer';
}): JSX.Element {
  const { data, isLoading, isFetching, error } = useScenariosList();
  const qc = useQueryClient();
  const { session } = useAuth();
  const licenseKey = session?.licenseId ?? 'anon';
  const refreshScenarios = useCallback(() => {
    void qc.invalidateQueries({ queryKey: ['scenarios', 'list', licenseKey] });
    void qc.invalidateQueries({ queryKey: ['scenarios', 'workfront-objects', licenseKey] });
    void qc.invalidateQueries({ queryKey: ['scenarios', 'custom-fields', licenseKey] });
    void qc.invalidateQueries({ queryKey: ['scenarios', 'planning-objects', licenseKey] });
    toast.success('Refreshing scenarios…');
  }, [qc, licenseKey]);
  const showSubTables = role !== 'business';
  // Actions column always renders — the Comment icon (Fusion Request)
  // is visible in every view mode (matches legacy
  // `DefaultTable.tsx > FusionRequestProvider`). The AI Summary +
  // Compare/Document dropdown ride the same `showActionsDropdown`
  // flag below; those are admin + developer only.
  const showActions = true;
  // Legacy `showDevLink` covers admin AND developer (combined toggle
  // in the legacy view-mode switch). New SPA splits the toggle three
  // ways but the dropdown column should still light up for both
  // privileged roles, not just developer.
  const showActionsDropdown = role !== 'business';
  const { byScenario: workfrontObjectsByScenario } = useScenarioWorkfrontObjects(showSubTables);
  const { byScenario: customFieldsByScenario } = useScenarioCustomFields(showSubTables);
  const { byScenario: planningObjectsByScenario } = useScenarioPlanningObjects(showSubTables);

  const allRows = useMemo<ReadonlyArray<ScenarioRowData>>(
    () => (data ?? []).map(asScenarioRow),
    [data],
  );

  // Toolbar filters are URL-pinned so a reload / shared link
  // restores the same view. Power-drawer filters (modules range,
  // ops range, modifiedBy, apps, tags) stay client-side — heavier
  // serialization without much sharing value. `?scenarioId=` keeps
  // its existing role as the modal trigger.
  const [params, setParams] = useSearchParams();
  const search = params.get('q') ?? '';
  const status = (params.get('status') ?? 'all') as ScenarioStatusFilter;
  const folder = params.get('folder') ?? FOLDER_ALL;
  const team = params.get('team') ?? TEAM_ALL;
  // Drawer filter (modules range, ops range, modifiedBy, apps, tags)
  // is URL-pinned via a base64url-JSON `?filter=` param. Decoding
  // happens on every render, but the cost is trivial and React lets
  // us keep the source of truth on the URL — no syncing dance.
  const filterEncoded = params.get('filter');
  const filter = useMemo<ScenarioFilterValue>(
    () => decodeScenarioFilter(filterEncoded),
    [filterEncoded],
  );
  // Sort + view preferences persist in localStorage so the operator
  // keeps their last layout across reloads + cross-tenant license
  // switches. Cheap to re-validate on hydration — guards against a
  // future shape change (renamed sort field) without a hard reset.
  const [sortField, setSortField] = usePersistentState<ScenarioSortField>(
    'wf-coe-app:scenarios:sort-field',
    'name',
    isSortField,
  );
  const [sortDirection, setSortDirection] = usePersistentState<ScenarioSortDirection>(
    'wf-coe-app:scenarios:sort-direction',
    'asc',
    isSortDirection,
  );
  const [view, setView] = usePersistentState<'default' | 'workfront-objects'>(
    'wf-coe-app:scenarios:view',
    'default',
    isViewMode,
  );

  /**
   * Patch a single URL param. Empty / default values delete the
   * param so the URL stays clean (`/scenarios` rather than
   * `/scenarios?q=&status=all&folder=__all__`). Functional updater
   * is required: calling several setters in the same tick reads the
   * same closure-pinned `params` otherwise and only the last update
   * survives.
   */
  const patchParam = useCallback(
    (key: string, value: string, defaultValue: string): void => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (!value || value === defaultValue) next.delete(key);
          else next.set(key, value);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );
  const setSearch = useCallback((v: string) => patchParam('q', v, ''), [patchParam]);
  const setStatus = useCallback(
    (v: ScenarioStatusFilter) => patchParam('status', v, 'all'),
    [patchParam],
  );
  const setFolder = useCallback((v: string) => patchParam('folder', v, FOLDER_ALL), [patchParam]);
  const setTeam = useCallback((v: string) => patchParam('team', v, TEAM_ALL), [patchParam]);
  const setFilter = useCallback(
    (next: ScenarioFilterValue) => {
      const encoded = encodeScenarioFilter(next);
      patchParam('filter', encoded, '');
    },
    [patchParam],
  );
  /**
   * Reset every URL-pinned filter in one shot, batched. Used by the
   * `Clear all` button on the active-filter strip + the surrounding
   * filter-cleanup helpers.
   */
  const clearUrlFilters = useCallback((): void => {
    setParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        next.delete('q');
        next.delete('status');
        next.delete('folder');
        next.delete('team');
        next.delete('filter');
        return next;
      },
      { replace: true },
    );
  }, [setParams]);

  const openScenarioId = params.get('scenarioId');
  const closeModal = (): void => {
    const next = new URLSearchParams(params);
    next.delete('scenarioId');
    setParams(next);
  };
  const openListingRow = useMemo<ScenarioRowData | undefined>(() => {
    if (!openScenarioId) return undefined;
    return allRows.find((r) => r.fusionId === openScenarioId);
  }, [openScenarioId, allRows]);
  const navigateScenario = useCallback(
    (id: string): void => {
      setParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.set('scenarioId', id);
          return next;
        },
        { replace: true },
      );
    },
    [setParams],
  );

  const folderOptions = useMemo<ReadonlyArray<string>>(() => {
    const names = new Set<string>();
    for (const r of allRows) {
      if (r.folderName && r.folderName.length > 0) names.add(r.folderName);
    }
    return [...names].sort((a, b) => a.localeCompare(b));
  }, [allRows]);

  // Teams come from `/v1/teams-info` (TeamInfo with name + fusionId).
  // We render the picker with whatever the engine returns, falling
  // back to any teams referenced by the scenario list itself (handles
  // a brief lag between scenario-list fetch + teams-info fetch on a
  // fresh login).
  const { data: teamsInfo } = useTeamsInfo();
  const teamOptions = useMemo(() => {
    const map = new Map<string, string>();
    if (teamsInfo) {
      for (const t of teamsInfo) {
        const fusionId = typeof t.fusionId === 'string' ? t.fusionId : null;
        const name = typeof t.name === 'string' ? t.name : null;
        if (fusionId && name) map.set(fusionId, name);
      }
    }
    for (const r of allRows) {
      if (r.teamFusionId && !map.has(r.teamFusionId)) {
        map.set(r.teamFusionId, `Team ${r.teamFusionId}`);
      }
    }
    return [...map.entries()]
      .map(([fusionId, name]) => ({ fusionId, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [teamsInfo, allRows]);
  const teamNamesByFusionId = useMemo(
    () => new Map(teamOptions.map((t) => [t.fusionId, t.name] as const)),
    [teamOptions],
  );

  // Pre-status filtered set. Drives both the counts on the
  // All / Active / Inactive chips *and* the final sorted list. The
  // chip counts deliberately use this set (not raw `allRows`) so
  // they reflect what the user would see if they switched tabs —
  // matches the legacy app's behaviour. Status is applied separately
  // below so the chip for the *currently selected* tab still shows
  // the same number as the row count above the table.
  // Threaded into matchesSearch so the per-Workfront-object / custom-
  // field / planning-object Search affordances inside the expand panel
  // find scenarios by sub-table content, not just by the row's surface
  // fields (matches legacy filterBySearch semantics).
  const searchContext = useMemo(
    () => ({
      workfrontObjectsByScenario,
      customFieldsByScenario,
      planningObjectsByScenario,
    }),
    [workfrontObjectsByScenario, customFieldsByScenario, planningObjectsByScenario],
  );

  const filteredByContext = useMemo(() => {
    return applyScenarioFilter(allRows, filter).filter((row) => {
      if (folder === FOLDER_UNCATEGORIZED) {
        if (row.folderName && row.folderName.length > 0) return false;
      } else if (folder !== FOLDER_ALL) {
        if (row.folderName !== folder) return false;
      }
      if (team !== TEAM_ALL && row.teamFusionId !== team) return false;
      if (!matchesSearch(row, search, searchContext)) return false;
      return true;
    });
  }, [allRows, folder, team, search, filter, searchContext]);

  const counts = useMemo(() => {
    const active = filteredByContext.filter(isActive).length;
    return {
      all: filteredByContext.length,
      active,
      inactive: filteredByContext.length - active,
    };
  }, [filteredByContext]);

  const filteredAndSorted = useMemo(() => {
    const filtered = filteredByContext.filter((row) => {
      if (status === 'active' && !isActive(row)) return false;
      if (status === 'inactive' && isActive(row)) return false;
      return true;
    });
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...filtered].sort((a, b) => compare(a, b, sortField) * dir);
  }, [filteredByContext, status, sortField, sortDirection]);

  const siblingFusionIds = useMemo<ReadonlyArray<string>>(
    () => filteredAndSorted.map((r) => r.fusionId),
    [filteredAndSorted],
  );

  // Shared CSV-export handler — wired into both the toolbar's
  // Export button and the table footer's Export dropdown. Exports
  // the *currently filtered* set so the file mirrors what the
  // operator sees on screen; filename is date-stamped to avoid
  // clobbering previous exports in the operator's Downloads folder.
  const handleExportCsv = useCallback((): void => {
    const csv = scenariosToCsv(filteredAndSorted);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCsv(`scenarios-${stamp}.csv`, csv);
  }, [filteredAndSorted]);

  const handleSortChange = (field: ScenarioSortField): void => {
    if (field === sortField) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  return (
    <div className="relative z-10 flex h-full flex-col">
      <ScenarioPageHeader
        folder={folder}
        onFolderChange={setFolder}
        folderOptions={folderOptions}
        team={team}
        onTeamChange={setTeam}
      />
      <ScenarioToolbar
        search={search}
        onSearchChange={setSearch}
        status={status}
        onStatusChange={setStatus}
        counts={counts}
        showViewSelect={role !== 'business'}
        view={view}
        onViewChange={setView}
        filterDrawer={<ScenarioFilterDrawer value={filter} onChange={setFilter} rows={allRows} />}
        onRefresh={refreshScenarios}
        isRefreshing={isFetching}
      />
      <ActiveFilterStrip
        search={search}
        onSearchClear={() => setSearch('')}
        folder={folder}
        onFolderClear={() => setFolder(FOLDER_ALL)}
        status={status}
        onStatusClear={() => setStatus('all')}
        team={team}
        onTeamClear={() => setTeam(TEAM_ALL)}
        teamNamesByFusionId={teamNamesByFusionId}
        filter={filter}
        onFilterChange={setFilter}
        rows={allRows}
        onClearAll={clearUrlFilters}
      />
      <div className="px-6 py-4">
        {view === 'workfront-objects' ? (
          <WorkfrontObjectsAllTable
            filteredScenarios={filteredAndSorted}
            workfrontObjectsByScenario={workfrontObjectsByScenario}
            customFieldsByScenario={customFieldsByScenario}
            planningObjectsByScenario={planningObjectsByScenario}
            onSearch={setSearch}
          />
        ) : (
          <ScenarioListTable
            rows={filteredAndSorted}
            isLoading={isLoading}
            error={error}
            sortField={sortField}
            sortDirection={sortDirection}
            onSortChange={handleSortChange}
            totalUnfiltered={allRows.length}
            showExpand={showSubTables}
            workfrontObjectsByScenario={workfrontObjectsByScenario}
            customFieldsByScenario={customFieldsByScenario}
            planningObjectsByScenario={planningObjectsByScenario}
            showActions={showActions}
            showActionsDropdown={showActionsDropdown}
            searchQuery={search}
            onSearch={setSearch}
            onExportCsv={handleExportCsv}
            canExportCsv={filteredAndSorted.length > 0}
          />
        )}
      </div>
      <ScenarioDetailModal
        fusionId={openScenarioId}
        listingRow={openListingRow}
        onClose={closeModal}
        role={role}
        siblingFusionIds={siblingFusionIds}
        onNavigate={navigateScenario}
      />
    </div>
  );
}

function isSortField(raw: string): raw is ScenarioSortField {
  return raw === 'name' || raw === 'description' || raw === 'modifiedAt' || raw === 'applications';
}

function isSortDirection(raw: string): raw is ScenarioSortDirection {
  return raw === 'asc' || raw === 'desc';
}

function isViewMode(raw: string): raw is 'default' | 'workfront-objects' {
  return raw === 'default' || raw === 'workfront-objects';
}

function compare(a: ScenarioRowData, b: ScenarioRowData, field: ScenarioSortField): number {
  switch (field) {
    case 'name':
      return (a.name ?? '').localeCompare(b.name ?? '');
    case 'description':
      return (a.description ?? '').localeCompare(b.description ?? '');
    case 'modifiedAt':
      return (a.modifiedAt ?? '').localeCompare(b.modifiedAt ?? '');
    case 'applications': {
      const an = a.applications?.[0]?.label ?? '';
      const bn = b.applications?.[0]?.label ?? '';
      const byFirst = an.localeCompare(bn);
      if (byFirst !== 0) return byFirst;
      return (a.applications?.length ?? 0) - (b.applications?.length ?? 0);
    }
    default:
      return 0;
  }
}
